import { useState, useEffect } from "react";
import axios from "axios";
import {
  Shield,
  Eye,
  EyeOff,
  Trash2,
  Download,
  RefreshCw,
  Users,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Building2,
  Loader2,
  ExternalLink,
  Info,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrivacyPanel({ userEmail, userPlan }) {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [policyInfo, setPolicyInfo] = useState(null);
  const [profileViewers, setProfileViewers] = useState(null);
  const [matches, setMatches] = useState([]);
  
  const [matchingVisible, setMatchingVisible] = useState(false);
  const [updatingVisibility, setUpdatingVisibility] = useState(false);
  
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (userEmail) {
      fetchData();
    }
  }, [userEmail]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [policyRes, dataRes, viewersRes, matchesRes] = await Promise.all([
        axios.get(`${API}/privacy/policy`),
        axios.get(`${API}/privacy/my-data/${encodeURIComponent(userEmail)}`).catch(() => ({ data: null })),
        axios.get(`${API}/privacy/who-viewed-me/${encodeURIComponent(userEmail)}`).catch(() => ({ data: null })),
        axios.get(`${API}/matching/my-matches/${encodeURIComponent(userEmail)}`).catch(() => ({ data: { matches: [] } })),
      ]);

      setPolicyInfo(policyRes.data);
      
      if (dataRes.data?.profile) {
        setProfile(dataRes.data.profile);
        setMatchingVisible(dataRes.data.profile.matching_visible || false);
      }
      
      if (viewersRes.data) {
        setProfileViewers(viewersRes.data);
      }
      
      setMatches(matchesRes.data?.matches || []);
    } catch (error) {
      console.error("Error fetching privacy data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMatching = async (visible) => {
    // Check if user has given consent
    if (visible && !profile?.privacy_consent_given) {
      setShowConsentDialog(true);
      return;
    }

    setUpdatingVisibility(true);
    try {
      await axios.post(`${API}/privacy/matching-visibility`, {
        email: userEmail,
        visible: visible,
      });
      
      setMatchingVisible(visible);
      toast.success(visible 
        ? "Tu perfil ahora es visible para matching" 
        : "Tu perfil ya no es visible para matching"
      );
      
      fetchData(); // Refresh data
    } catch (error) {
      console.error("Error updating visibility:", error);
      toast.error(error.response?.data?.detail || "Error al actualizar visibilidad");
    } finally {
      setUpdatingVisibility(false);
    }
  };

  const handleGiveConsent = async () => {
    try {
      await axios.post(`${API}/privacy/consent`, {
        email: userEmail,
        consent_type: "privacy_policy",
        consent_given: true,
      });
      
      toast.success("Consentimiento registrado");
      setShowConsentDialog(false);
      
      // Now enable matching
      await handleToggleMatching(true);
      fetchData();
    } catch (error) {
      console.error("Error recording consent:", error);
      toast.error("Error al registrar consentimiento");
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/privacy/my-data/${encodeURIComponent(userEmail)}`);
      
      // Create downloadable JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement("a");
      link.href = url;
      link.download = `mis-datos-talentmatcher-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Datos exportados correctamente");
      setShowExportDialog(false);
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error("Error al exportar datos");
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteData = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/privacy/delete-my-data`, {
        data: { email: userEmail, confirm: true }
      });
      
      toast.success("Todos tus datos han sido eliminados");
      setShowDeleteDialog(false);
      
      // Redirect to home or logout
      window.location.href = "/";
    } catch (error) {
      console.error("Error deleting data:", error);
      toast.error(error.response?.data?.detail || "Error al eliminar datos");
    } finally {
      setDeleting(false);
    }
  };

  const handleRenewConsent = async () => {
    try {
      await axios.post(`${API}/privacy/renew-consent/${encodeURIComponent(userEmail)}`);
      toast.success("Consentimiento renovado por 12 meses");
      fetchData();
    } catch (error) {
      console.error("Error renewing consent:", error);
      toast.error("Error al renovar consentimiento");
    }
  };

  const handleRespondToMatch = async (matchId, interested) => {
    try {
      await axios.post(`${API}/matching/respond/${matchId}?interested=${interested}&email=${encodeURIComponent(userEmail)}`);
      toast.success(interested ? "¡Respuesta enviada al reclutador!" : "Respuesta registrada");
      fetchData();
    } catch (error) {
      console.error("Error responding to match:", error);
      toast.error("Error al responder");
    }
  };

  // Calculate days until data expires
  const getDaysUntilExpiry = () => {
    if (!profile?.data_retention_expires_at) return null;
    const expiryDate = new Date(profile.data_retention_expires_at);
    const now = new Date();
    const diffTime = expiryDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilExpiry = getDaysUntilExpiry();
  const expiryPercentage = daysUntilExpiry ? Math.max(0, Math.min(100, (daysUntilExpiry / 365) * 100)) : 0;

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <CardTitle className="font-heading">Privacidad y Datos Personales</CardTitle>
              <CardDescription>
                Gestiona tus datos y preferencias de visibilidad
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Matching Visibility Toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            {matchingVisible ? (
              <Eye className="w-5 h-5 text-green-600" />
            ) : (
              <EyeOff className="w-5 h-5 text-muted-foreground" />
            )}
            Visibilidad para Matching
          </CardTitle>
          <CardDescription>
            Cuando está activo, tu perfil puede ser descubierto por reclutadores que buscan candidatos con tu perfil
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="font-medium">
                {matchingVisible ? "Tu perfil es visible" : "Tu perfil está oculto"}
              </p>
              <p className="text-sm text-muted-foreground">
                {matchingVisible 
                  ? "Reclutadores pueden encontrarte en búsquedas de candidatos"
                  : "Solo tú puedes ver tu perfil y aplicar a puestos manualmente"
                }
              </p>
            </div>
            <Switch
              checked={matchingVisible}
              onCheckedChange={handleToggleMatching}
              disabled={updatingVisibility}
            />
          </div>
          
          {matchingVisible && profile?.matching_visible_since && (
            <p className="text-xs text-muted-foreground mt-3">
              Visible desde: {new Date(profile.matching_visible_since).toLocaleDateString("es-PA")}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Data Retention Status */}
      {profile?.data_retention_expires_at && (
        <Card className={daysUntilExpiry && daysUntilExpiry < 30 ? "border-amber-500" : ""}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Retención de Datos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm">Tiempo restante</span>
              <span className="font-medium">
                {daysUntilExpiry > 0 ? `${daysUntilExpiry} días` : "Expirado"}
              </span>
            </div>
            <Progress value={expiryPercentage} className="h-2" />
            
            {daysUntilExpiry && daysUntilExpiry < 30 && (
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                      Tus datos serán eliminados pronto
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      Renueva tu consentimiento para mantener tu perfil activo
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <Button variant="outline" onClick={handleRenewConsent} className="w-full">
              <RefreshCw className="w-4 h-4 mr-2" />
              Renovar por 12 meses más
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Profile Viewers */}
      {profileViewers && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Quién ha visto tu perfil
            </CardTitle>
            <CardDescription>
              Transparencia sobre quién accede a tu información
            </CardDescription>
          </CardHeader>
          <CardContent>
            {profileViewers.total_views === 0 ? (
              <p className="text-muted-foreground text-center py-4">
                Nadie ha visto tu perfil todavía
              </p>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between text-sm">
                  <span>Total de visualizaciones</span>
                  <Badge variant="secondary">{profileViewers.total_views}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>Empresas únicas</span>
                  <Badge variant="secondary">{profileViewers.unique_companies}</Badge>
                </div>
                
                {profileViewers.views_by_company?.length > 0 && (
                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm font-medium mb-3">Por empresa:</p>
                    <div className="space-y-2">
                      {profileViewers.views_by_company.slice(0, 5).map((company, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-muted-foreground" />
                            <span>{company.company}</span>
                          </div>
                          <span className="text-muted-foreground">{company.view_count} veces</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Job Matches */}
      {matches.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Oportunidades que coinciden contigo
            </CardTitle>
            <CardDescription>
              Puestos donde tu perfil tiene alta compatibilidad
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {matches.slice(0, 5).map((match) => (
                <div key={match.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium">{match.job_title}</h4>
                      <Badge variant="outline" className="mt-1">
                        {match.match_score}% match
                      </Badge>
                    </div>
                    {match.candidate_interested === null && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleRespondToMatch(match.id, true)}
                        >
                          Me interesa
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRespondToMatch(match.id, false)}
                        >
                          No gracias
                        </Button>
                      </div>
                    )}
                    {match.candidate_interested === true && (
                      <Badge className="bg-green-100 text-green-800">Interesado</Badge>
                    )}
                    {match.candidate_interested === false && (
                      <Badge variant="secondary">Descartado</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rights Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Tus Derechos
          </CardTitle>
          <CardDescription>
            Tienes control total sobre tu información personal
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="access">
              <AccordionTrigger>Derecho de Acceso</AccordionTrigger>
              <AccordionContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Puedes solicitar y obtener una copia de todos los datos personales que tenemos sobre ti.
                </p>
                <Button variant="outline" size="sm" onClick={() => setShowExportDialog(true)}>
                  <Download className="w-4 h-4 mr-2" />
                  Exportar mis datos
                </Button>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="rectification">
              <AccordionTrigger>Derecho de Rectificación</AccordionTrigger>
              <AccordionContent>
                <p className="text-sm text-muted-foreground">
                  Puedes actualizar o corregir tus datos personales en cualquier momento desde tu perfil.
                </p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="opposition">
              <AccordionTrigger>Derecho de Oposición</AccordionTrigger>
              <AccordionContent>
                <p className="text-sm text-muted-foreground">
                  Puedes oponerte al tratamiento de tus datos desactivando la visibilidad para matching arriba.
                </p>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="deletion">
              <AccordionTrigger>Derecho de Cancelación</AccordionTrigger>
              <AccordionContent>
                <p className="text-sm text-muted-foreground mb-3">
                  Puedes solicitar la eliminación total e irreversible de todos tus datos personales.
                </p>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar todos mis datos
                </Button>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      {/* Privacy Policy Link */}
      <Card className="bg-muted/50">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm">
                Política de Privacidad v{policyInfo?.version || "1.0"}
              </span>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <a href="/docs/POLITICA_PRIVACIDAD.md" target="_blank">
                <ExternalLink className="w-4 h-4 mr-2" />
                Ver política completa
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Consent Dialog */}
      <Dialog open={showConsentDialog} onOpenChange={setShowConsentDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Consentimiento para Matching</DialogTitle>
            <DialogDescription>
              Para activar la visibilidad de tu perfil, necesitamos tu consentimiento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted p-4 rounded-lg text-sm space-y-2">
              <p><strong>Al activar esta opción, consientes que:</strong></p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Tu perfil sea visible para reclutadores que buscan candidatos</li>
                <li>Reclutadores puedan ver tu información de contacto si hay un match</li>
                <li>Recibas notificaciones sobre oportunidades laborales relevantes</li>
                <li>Tus datos se conserven por 12 meses desde tu última actividad</li>
              </ul>
            </div>
            <p className="text-sm text-muted-foreground">
              Puedes revocar este consentimiento en cualquier momento desactivando la visibilidad.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConsentDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleGiveConsent}>
              Acepto y activar visibilidad
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Exportar mis datos</DialogTitle>
            <DialogDescription>
              Recibirás un archivo JSON con todos tus datos personales almacenados.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleExportData} disabled={exporting}>
              {exporting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Exportando...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Descargar datos</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              ¿Eliminar todos tus datos?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>Esta acción es <strong>irreversible</strong>. Se eliminarán:</p>
                <ul className="list-disc list-inside text-sm">
                  <li>Tu perfil completo</li>
                  <li>Tu CV y datos extraídos</li>
                  <li>Historial de aplicaciones</li>
                  <li>Optimizaciones de CV</li>
                </ul>
                <p className="text-destructive font-medium mt-2">
                  No podrás recuperar esta información.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteData}
              disabled={deleting}
            >
              {deleting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Eliminando...</>
              ) : (
                "Sí, eliminar todo"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
